/** 可按装配离线包规则识别的文件摘要。 */
export interface AssemblyOfflinePackageFileLike {
  /** 后端文件 ID 或容器内路径。 */
  readonly fileId: string
  /** 后端文件 UUID；部分历史文件可能为空。 */
  readonly uuid?: string
  /** 后端文件类型标记。 */
  readonly kind?: string
}

/** 解析唯一装配离线包的输入。 */
export interface ResolveUniqueAssemblyOfflinePackageFileInput<
  TFile extends AssemblyOfflinePackageFileLike,
> {
  /** 当前节点携带的文件列表。 */
  readonly files: readonly TFile[]
  /** 错误日志中的仓库 ID。 */
  readonly repositoryId?: string
  /** 错误日志中的提交 ID。 */
  readonly commitId?: string
}

const ASSEMBLY_OFFLINE_PACKAGE_KIND = '.puppy'

/**
 * 判断文件是否是装配节点离线包。
 *
 * @param file 可用于识别的文件摘要。
 * @returns fileId 后缀或 kind 命中 `.puppy` 时返回 true。
 */
export function isAssemblyOfflinePackageFile(file: AssemblyOfflinePackageFileLike): boolean {
  const normalizedFileId = file.fileId.trim().toLowerCase()
  const normalizedKind = file.kind?.trim().toLowerCase() ?? ''

  return normalizedFileId.endsWith(ASSEMBLY_OFFLINE_PACKAGE_KIND)
    || normalizedKind === ASSEMBLY_OFFLINE_PACKAGE_KIND
}

/**
 * 格式化不可判定的重复候选，保留定位后端快照所需的原始身份。
 *
 * @param file 命中的离线包候选。
 * @returns 可放入错误消息的文件摘要。
 */
function formatOfflinePackageCandidate(file: AssemblyOfflinePackageFileLike): string {
  return `fileId=${file.fileId}, uuid=${file.uuid ?? ''}, kind=${file.kind ?? ''}`
}

/**
 * 从文件列表中解析唯一的装配 `.puppy` 离线包。
 *
 * 多个候选意味着输入快照不可判定，SDK 必须统一拒绝，避免不同调用入口各自选择。
 *
 * @param input 文件列表与错误定位上下文。
 * @returns 唯一离线包；没有候选时返回 undefined。
 * @throws 当文件列表中存在多个 `.puppy` 离线包候选时。
 */
export function resolveUniqueAssemblyOfflinePackageFile<
  TFile extends AssemblyOfflinePackageFileLike,
>(input: ResolveUniqueAssemblyOfflinePackageFileInput<TFile>): TFile | undefined {
  const packageFiles = input.files.filter(isAssemblyOfflinePackageFile)

  if (packageFiles.length > 1) {
    throw new Error(
      'multiple .puppy files: '
      + `repositoryId=${input.repositoryId ?? ''}, commitId=${input.commitId ?? ''}, `
      + `candidates=[${packageFiles.map(formatOfflinePackageCandidate).join('; ')}]`,
    )
  }

  return packageFiles[0]
}
